<script setup lang="ts">
import { computed } from 'vue';
import { useTransitions, type TransitionConfig } from '@/helpers/transitions';

/**
 * Props for the TransitionWrapper component
 * @interface Props
 */
interface Props {
	/** CSS transition name for Vue's transition component */
	name?: string;
	/** Transition mode - 'out-in' waits for leaving element to finish before entering, 'in-out' does the opposite */
	mode?: 'out-in' | 'in-out';
	/** Duration of the transition in milliseconds */
	duration?: number;
	/** CSS easing function (e.g., 'ease-in-out', 'ease-out') */
	easing?: string;
	/** Predefined transition type */
	type?: 'fade' | 'slide' | 'scale' | 'tab' | 'custom';
	/** Direction for slide transitions */
	direction?: 'left' | 'right' | 'up' | 'down';
	/** Custom transition configuration (used when type is 'custom') */
	customConfig?: TransitionConfig;
}

/**
 * Reusable transition wrapper component that provides consistent animations
 * throughout the application. Automatically handles accessibility preferences.
 *
 * @example
 * ```vue
 * <template>
 *   <!-- Simple fade transition -->
 *   <TransitionWrapper type="fade" mode="out-in">
 *     <div v-if="showContent">Content here</div>
 *   </TransitionWrapper>
 *
 *   <!-- Slide transition -->
 *   <TransitionWrapper type="slide" direction="right" mode="out-in">
 *     <div v-if="showPanel">Panel content</div>
 *   </TransitionWrapper>
 *
 *   <!-- Custom transition -->
 *   <TransitionWrapper
 *     type="custom"
 *     :custom-config="{ duration: 500, easing: 'ease-out' }"
 *     mode="out-in"
 *   >
 *     <div v-if="showModal">Modal content</div>
 *   </TransitionWrapper>
 * </template>
 * ```
 */
const props = withDefaults(defineProps<Props>(), {
	name: 'transition',
	mode: 'out-in',
	duration: 300,
	easing: 'ease-in-out',
	type: 'fade',
	direction: 'right',
});

const {
	createTransitionClasses,
	createSlideTransition,
	createFadeTransition,
	createScaleTransition,
	createTabTransition,
} = useTransitions();

/**
 * Computed property that returns the appropriate transition classes based on the selected type
 * Automatically handles all transition types and custom configurations
 */
const transitionClasses = computed<ReturnType<typeof createTransitionClasses>>(() => {
	switch (props.type) {
		case 'slide':
			return createSlideTransition(props.direction);
		case 'scale':
			return createScaleTransition();
		case 'tab':
			return createTabTransition();
		case 'custom':
			return createTransitionClasses(props.customConfig);
		case 'fade':
		default:
			return createFadeTransition();
	}
});
</script>

<template>
	<transition :name="name" :mode="mode" v-bind="transitionClasses">
		<slot />
	</transition>
</template>
